import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, CoinsHand, Dotpoints01, DotsGrid, EmptyState, Plus, Select, TextInput, Ticket01, Trash01 } from "@opendate-app/setlist";
import { useMembershipProductNewContext } from "./MembershipProductNewContainer";
import {
  DISPLAY,
  DOOR_ADMISSION,
  MAX_SCANS_PER_DAY,
  NO_ORGANIZER_FEES,
  benefitKey,
  isDoorAdmission,
  kindChange,
  scansErrorKey,
  visibleBenefits,
} from "./membershipFormLogic";

const KIND_OPTIONS = [
  { value: DISPLAY, label: "Display only" },
  { value: DOOR_ADMISSION, label: "Door admission" },
  { value: NO_ORGANIZER_FEES, label: "No organizer fees" },
];

const KIND_ICONS = { [DISPLAY]: Dotpoints01, [DOOR_ADMISSION]: Ticket01, [NO_ORGANIZER_FEES]: CoinsHand };

const PLACEHOLDERS = {
  [DISPLAY]: "e.g. Early entry on show nights",
  [DOOR_ADMISSION]: "e.g. Free general admission",
  [NO_ORGANIZER_FEES]: "e.g. No organizer fees on tickets",
};

const BenefitRow = ({ benefit, index, error, scansError, takenKinds, disabled, onChange, onRemove }) => {
  const key = benefitKey(benefit);
  const doorAdmission = isDoorAdmission(benefit);
  const kindOptions = KIND_OPTIONS.map((option) =>
    takenKinds.includes(option.value) && option.value !== benefit.kind ? { ...option, disabled: true, supportingText: "In use" } : option
  );
  const KindIcon = KIND_ICONS[benefit.kind] || Dotpoints01;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: key, disabled });

  return (
    <div
      ref={setNodeRef}
      className={`membership-benefit${isDragging ? " is-dragging" : ""}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Button
        variant="tertiary"
        size="xs"
        icon={DotsGrid}
        aria-label={`Reorder benefit ${index + 1}. Use the arrow keys to move it.`}
        className="membership-benefit-grip"
        {...attributes}
        {...listeners}
      />
      <span className="membership-benefit-icon" aria-hidden="true">
        <KindIcon width={16} height={16} />
      </span>
      <div className="membership-benefit-input">
        <TextInput
          id={`membership-benefit-${key}`}
          aria-label={`Benefit ${index + 1}`}
          placeholder={PLACEHOLDERS[benefit.kind] || PLACEHOLDERS[DISPLAY]}
          value={benefit.description}
          error={error}
          onChange={(e) => onChange({ description: e.target.value })}
        />
        <div className="membership-benefit-controls">
          <div className="membership-benefit-kind">
            <Select
              id={`membership-benefit-kind-${key}`}
              aria-label={`Type of benefit ${index + 1}`}
              size="sm"
              options={kindOptions}
              disabled={disabled}
              value={benefit.kind}
              onChange={(kind) => {
                if (kind === benefit.kind) return;
                onChange(kindChange(benefit, kind));
              }}
            />
          </div>
          {doorAdmission ? (
            <div className="membership-benefit-scans">
              <TextInput
                id={`membership-benefit-scans-${key}`}
                aria-label={`Door scans a day for benefit ${index + 1}`}
                type="number"
                min={1}
                max={MAX_SCANS_PER_DAY}
                step={1}
                size="sm"
                trailingText="scans a day"
                value={benefit.scans_per_day ?? ""}
                error={scansError}
                onChange={(e) => onChange({ scans_per_day: e.target.value === "" ? null : Number(e.target.value) })}
              />
            </div>
          ) : null}
        </div>
      </div>
      <Button
        variant="tertiary"
        size="xs"
        icon={Trash01}
        aria-label={`Remove benefit ${index + 1}`}
        className="membership-benefit-remove"
        onClick={onRemove}
      />
    </div>
  );
};

BenefitRow.propTypes = {
  benefit: PropTypes.shape({
    description: PropTypes.string,
    kind: PropTypes.oneOf([DISPLAY, DOOR_ADMISSION, NO_ORGANIZER_FEES]).isRequired,
    scans_per_day: PropTypes.number,
  }).isRequired,
  index: PropTypes.number.isRequired,
  error: PropTypes.string,
  scansError: PropTypes.string,
  takenKinds: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const BenefitsSection = ({ errors, disabled }) => {
  const { membershipProduct, addBenefit, updateBenefit, removeBenefit, reorderBenefits } = useMembershipProductNewContext();
  const all = membershipProduct.membership_product_benefits;
  const shown = visibleBenefits(membershipProduct);
  const ids = shown.map(benefitKey);
  const takenKinds = [DOOR_ADMISSION, NO_ORGANIZER_FEES].filter((kind) => shown.some((benefit) => benefit.kind === kind));
  const focusNew = useRef(false);
  const add = () => {
    focusNew.current = true;
    addBenefit();
  };
  useEffect(() => {
    if (!focusNew.current || !ids.length) return;
    focusNew.current = false;
    const input = document.getElementById(`membership-benefit-${ids[ids.length - 1]}`);
    if (input) input.focus();
  }, [ids.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = all.indexOf(shown[ids.indexOf(String(active.id))]);
    const to = all.indexOf(shown[ids.indexOf(String(over.id))]);
    if (from !== -1 && to !== -1) reorderBenefits(from, to);
  };

  if (!shown.length) {
    return (
      <EmptyState
        size="sm"
        icon={Dotpoints01}
        title="No benefits yet"
        description="Add a line for each perk fans get, like early entry or a discount at the bar."
        secondaryAction={{ label: "Add benefit", icon: Plus, onClick: add }}
      />
    );
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="membership-benefits">
            {shown.map((benefit, index) => (
              <BenefitRow
                key={ids[index]}
                benefit={benefit}
                index={index}
                error={errors[`benefit:${ids[index]}`]}
                scansError={errors[scansErrorKey(benefit)]}
                takenKinds={takenKinds}
                disabled={disabled}
                onChange={(changes) => updateBenefit(all.indexOf(benefit), changes)}
                onRemove={() => removeBenefit(all.indexOf(benefit))}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div>
        <Button variant="secondary" size="sm" icon={Plus} onClick={add}>
          Add benefit
        </Button>
      </div>
    </>
  );
};

BenefitsSection.propTypes = {
  errors: PropTypes.objectOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
};

export default BenefitsSection;
